import {
  DrupalPage,
  Benefit,
  Config,
  Season,
  iTicketWidget,
} from '../../types/drupal';
import { AccommodationsResponse, RoomInfo } from '../../types/accommodation';
import {
  CustomerData,
  Guest,
  SkiDay,
  CDPReference,
} from '../../types/customer';
import { RentleOrder } from '../../types/rentle';
import {
  RoomTypeAvailabilityResponse,
  SingleRoomAvailabilityResponse,
} from '../../types/accommodation';

type UpdatePassengersResponse = {
  status: string;
  errorMessage?: string;
}

/**
 * Fetches content from drupal jsonapi, through the node app.
 */
export async function fetchPageContent(
  pageId: string,
  lang = 'fi'
): Promise<DrupalPage | null> {
  const domain = getDomain();
  const url = `${process.env.REACT_APP_NODE_URL}/getPageContent?id=${pageId}&lang=${lang}&resort=${domain}`;

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/vnd.api+json',
        'Content-Type': 'application/vnd.api+json',
      },
      credentials: 'include',
    });

    if (response.status === 200) {
      const data = await response.json();
      return data;
    }
  } catch (error) {
    console.log(error);
  }

  return null;
}

/**
 * Fetches benefits taxonomy terms from drupal.
 */
export async function fetchBenefits(
  lang = 'fi',
  token: string
): Promise<Benefit[] | null> {
  const url = `${
    process.env.REACT_APP_NODE_URL
  }/getBenefits?lang=${lang}&resort=${getDomain()}`;

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/vnd.api+json',
        'Content-Type': 'application/vnd.api+json',
        token,
      },
      credentials: 'include',
    });

    if (response.status === 200) {
      const data = await response.json();
      return data;
    }
  } catch (error) {
    console.log(error);
  }

  return null;
}

/**
 * Gets the customer data from IPS, through the node app.
 */
export async function fetchCustomerData(
  token: string,
  profileOnly = false,
  sources?: string
): Promise<CustomerData | null> {
  const params = new URLSearchParams({
    profileOnly: profileOnly.toString(),
  });

  if (sources) {
    params.append('sources', sources);
  }

  try {
    const response = await fetch(
      `${process.env.REACT_APP_NODE_URL}/getCustomer?` + params.toString(),
      {
        method: 'GET',
        headers: {
          Accept: 'application/vnd.api+json',
          'Content-Type': 'application/vnd.api+json',
          token,
        },
      }
    );

    if (response.status === 200) {
      const data = await response.json();
      return data;
    }
  } catch (error) {
    console.log(error);
  }

  return null;
}

/**
 * Fetcha availabilities for a room type.
 */
export async function fetchAvailabilities(params: {
  token: string;
  roomTypeId: string;
  startDate: string;
  endDate: string;
  resort?: string;
}): Promise<RoomTypeAvailabilityResponse[]> {
  const { token, roomTypeId, startDate, endDate } = params;
  const resort = params.resort
    ? params.resort
    : getDomain() === 'ruka'
    ? 'Ruka'
    : 'Pyha';
  const url = new URL(`${process.env.REACT_APP_NODE_URL}/getAvailabilities`);
  url.searchParams.append('roomTypeId', roomTypeId);
  url.searchParams.append('startDate', startDate);
  url.searchParams.append('endDate', endDate);
  url.searchParams.append('resort', resort);

  try {
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        token,
      },
    });

    if (response.status === 200) {
      const data = await response.json();
      return data;
    }
  } catch (error) {
    console.log(error);
  }
  return [];
}

/**
 * Fetch availability for a single room.
 */
export async function fetchRoomAvailability(params: {
  token: string;
  roomId: string;
  startDate: string;
  endDate: string;
}): Promise<SingleRoomAvailabilityResponse | null> {
  const { token, roomId, startDate, endDate } = params;
  const url = new URL(
    `${process.env.REACT_APP_NODE_URL}/getSingleRoomAvailability`
  );
  url.searchParams.append('roomId', roomId);
  url.searchParams.append('startDate', startDate);
  url.searchParams.append('endDate', endDate);

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        token,
      },
    });

    if (response.status === 200) {
      const data = await response.json();
      return data;
    }
  } catch (error) {
    console.log(error);
  }
  return null;
}

export async function fetchAccommodations({
  reservationType = 0,
  token = '',
  resort = getDomain() === 'ruka' ? 'Ruka' : 'Pyha',
}): Promise<AccommodationsResponse | null> {
  let url = `${process.env.REACT_APP_NODE_URL}/getAccommodations?resort=${resort}`;

  if (reservationType !== 0) {
    url += `&reservationType=${reservationType}`;
  }

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        token,
      },
    });

    if (response.status === 200) {
      const data = await response.json();
      return data;
    }
  } catch (error) {
    console.log(error);
  }
  return null;
}

/**
 * Pre-define some commonly used color variables related to brand.
 */
export function getColorVars() {
  const domain = getDomain();

  // Seems like colors have to be explicitly defined like this, or
  // tailwind won't recognize them.
  let bgColor = 'bg-red-500';
  let textColor = 'text-red-500';
  let borderColor = 'border-red-500';
  let hoverBgColor = 'hover:bg-red-500';

  if (domain === 'pyha') {
    bgColor = 'bg-pyhablue-500';
    textColor = 'text-pyhablue-500';
    borderColor = 'border-pyhablue-500';
    hoverBgColor = 'hover:bg-pyhablue-500';
  }

  return {
    bgColor,
    textColor,
    borderColor,
    hoverBgColor,
  };
}

/**
 * Return domain name.
 */
export function getDomain(): string {
  let domain = window.location.hostname.includes('pyha') ? 'pyha' : 'ruka';
  if (process.env.REACT_APP_FORCE_DOMAIN) {
    domain = process.env.REACT_APP_FORCE_DOMAIN;
  }

  return domain;
}

/**
 * Fetches rentle orders from IPS.
 */
export async function fetchRentleOrders(
  email: string,
  token: string
): Promise<RentleOrder[] | null> {
  const url = `${process.env.REACT_APP_NODE_URL}/getRentleOrders?email=${email}`;

  try {
    const response = await fetch(url, {
      headers: {
        token,
      },
    });

    if (response.status === 200) {
      const data = await response.json();
      return data;
    }
  } catch (error) {
    console.log(error);
    return null;
  }

  return null;
}

/**
 * Fetch room info.
 */
export async function fetchRoom(
  roomId: string,
  roomTypeId: string,
  token: string
): Promise<RoomInfo | null> {
  const url = `${process.env.REACT_APP_NODE_URL}/getRoom?roomId=${roomId}&roomTypeId=${roomTypeId}`;

  try {
    const response = await fetch(url, {
      headers: { token },
    });

    if (response.status === 200) {
      const data = await response.json();
      return data;
    }
  } catch (error) {
    console.log(error);
  }

  return null;
}

/**
 * Fetch Omatunturi config.
 */
export async function fetchConfig(
  lang = 'fi',
  token: string
): Promise<Config | null> {
  const url = `${
    process.env.REACT_APP_NODE_URL
  }/getConfig?lang=${lang}&resort=${getDomain()}`;

  try {
    const response = await fetch(url, { headers: { token } });

    if (response.status === 200) {
      const data = await response.json();
      return data;
    }
  } catch (error) {
    console.log(error);
  }
  return null;
}

/**
 * Get seasons from drupal.
 */
export async function fetchSeasons(lang: string): Promise<Season[] | null> {
  const resort = getDomain();

  const url = `${process.env.REACT_APP_NODE_URL}/getSeasons?resort=${resort}&lang=${lang}`;

  try {
    const response = await fetch(url);
    if (response.ok) {
      const seasons = await response.json();
      return seasons;
    }
  } catch (error) {
    console.log(error);
  }

  return null;
}

export async function fetchSkidays(
  id: string,
  token: string
): Promise<SkiDay[] | null> {
  const url = `${process.env.REACT_APP_NODE_URL}/getSkiDays?id=${id}`;

  try {
    const response = await fetch(url, {
      headers: {
        token,
      },
    });
    if (response.status === 200) {
      const data = await response.json();
      return data;
    } else if (response.status === 404) {
      return [];
    }
  } catch (error) {
    console.log(error);
  }
  return null;
}

/**
 * Release quota.
 */
export async function releaseQuota(
  email: string,
  season: string,
  lang = 'fi',
  productName: string,
  token: string
): Promise<boolean> {
  const url = `${process.env.REACT_APP_NODE_URL}/releaseQuota`;

  const body = {
    email,
    season,
    lang,
    resort: getDomain(),
    productName,
  };
  try {
    const response = await fetch(url, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: {
        token,
      },
    });

    if (response.status === 200) {
      return true;
    }
  } catch (error) {
    console.log(error);
  }

  return false;
}

/**
 * Update passengers.
 */
export async function updatePassengers(
  resort: string,
  guests: Guest[],
  hotellinxReservationLineId: string,
  reservationLineId: string | undefined,
  token: string
): Promise<UpdatePassengersResponse> {
  if (!hotellinxReservationLineId && !reservationLineId) {
    throw new Error(
      'Both hotellinxReservationLineId and reservationLineId cannot be missing from the request.'
    );
  }

  const url = `${process.env.REACT_APP_NODE_URL}/updateReservation`;

  let body;
  if (!reservationLineId) {
    body = {
      resort,
      hotellinxReservationLineId,
      hlxGuestNames: {
        hlxGuestName: guests,
      },
    };
  } else {
    body = {
      resort,
      hotellinxReservationLineId,
      drupalOrderItem: reservationLineId,
      hlxGuestNames: {
        hlxGuestName: guests,
      },
    };
  }
  try {
    const response = await fetch(url, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: {
        token,
      },
    });

    if (response.status === 200) {
      return { status: "200" };
    }
    // Return information about the error 
    else {
      const data = await response.json();
      return {
        status: response.status.toString(),
        errorMessage: data,
      };
    }
  } catch (error) {
    console.log(error);
    throw new Error((error as Error).message);
  }
}

/**
 * Get code and username from CDP references data
 * and return them as url parameters if found.
 */
export function parseITicketUserData(
  references: CDPReference[]
): string | null {
  const resort = getDomain();
  const code = getCDPReferenceValue(references, `code_${resort}`);
  const userName = getCDPReferenceValue(references, `userName_${resort}`);

  if (code && userName) {
    const params = new URLSearchParams({
      userName: userName,
      code: code,
    });

    return params.toString();
  }

  return null;
}

/**
 * Find property by name from references and return
 * its value.
 */
export function getCDPReferenceValue(
  references: CDPReference[],
  property: string
) {
  const targetRef = references.find(
    (reference) => reference.property === property
  );

  if (targetRef) {
    return targetRef.value;
  }

  return null;
}

/**
 * Fetch iTicket widgets. Either belonging to a specific reservation,
 * or to all the orders the user has.
 */
export async function fetchITicketWidgets(
  startDate: string | null,
  endDate: string | null,
  productId: string | number | null,
  showAlwaysOn: string,
  token: string,
  reservationId?: number,
  resort?: string
): Promise<iTicketWidget[] | null> {
  const domain = resort ?? getDomain();
  let url = `${process.env.REACT_APP_NODE_URL}/getITicketCampaigns?resort=${domain}&showAlwaysOn=${showAlwaysOn}`;

  // Will return widgets applicable to one specific reservation.
  // If one of these is empty, all campaigns for all orders will be fetched.
  if (startDate && endDate && productId) {
    url += `&startDate=${startDate}&endDate=${endDate}&productId=${productId}`;
  }

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        token,
      },
    });

    if (response.status === 200) {
      let data = await response.json();

      if (reservationId) {
        data = data.map((widget: iTicketWidget) => {
          // Adding reservation id for easier mapping, when fetching
          // widgets for reservations.
          const widgetWithReservationId = widget;
          widgetWithReservationId.reservationId = reservationId;
          return widgetWithReservationId;
        });
      }

      return data;
    }
  } catch (error) {
    console.log(error);
    return null;
  }

  return null;
}

/**
 * Send user token in header and fetch iTicket magic link
 * for redirecting the user.
 */
export async function fetchITicketMagicLink(
  token: string,
  language: string
): Promise<string | null> {
  const url = `${process.env.REACT_APP_NODE_URL}/iTicketLogin`;

  const body = {
    resort: getDomain(),
    language,
  };

  try {
    const response = await fetch(url, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
        token,
      },
    });

    if (response.status ===  201 || response.status === 200 ) {
      const { url: redirectUrl } = await response.json();

      return redirectUrl;
    }
  } catch (error) {
    console.log(error);
  }
  return null;
}

/**
 * Appends the param to the url which indicates drupal
 * should redirect the user to the login page if they're
 * not logged in.
 */
export function getDrupalLink(url: string): string {
  const link = new URL(url);
  link.searchParams.append('login', 'true');

  return link.toString();
}
